/*       */

import { loadData, mutateData, queryData } from 'app/utils/redux/action-utils';
import { get } from 'app/utils/lo/lo';
import Immutable from 'app/utils/immutable/Immutable';
import { showToastr } from 'store/actions/app/appActions';

import llmSettingsQuery from 'graphql/llm/llmSettingsQuery';
import updateLlmSettingsMutation from 'graphql/llm/updateLlmSettingsMutation';

export const LOAD_LLM_PROVIDERS_STARTED = '@@affectli/admin/llmProviders/LOAD_LLM_PROVIDERS_STARTED';
export const LOAD_LLM_PROVIDERS = '@@affectli/admin/llmProviders/LOAD_LLM_PROVIDERS';

export const UPDATE_LLM_SETTINGS_STARTED = '@@affectli/admin/llmProviders/UPDATE_LLM_SETTINGS_STARTED';
export const UPDATE_LLM_SETTINGS = '@@affectli/admin/llmProviders/UPDATE_LLM_SETTINGS';

// The `llm` settings section stores { providers: [...] }. Map it into the
// { records, count } shape expected by dataTableReducer.
const toProviders = (payload) => {
    const providers = get(payload, 'providers') || [];
    return { records: providers, count: providers.length };
};

// Keep only the fields the backend accepts; empty baseUrl -> null (official endpoint).
const pickProvider = p => ({
    id: p.id,
    apiFormat: p.apiFormat,
    apiKey: p.apiKey,
    baseUrl: p.baseUrl == null || p.baseUrl === '' ? null : p.baseUrl,
});

export const loadLlmProviders = loadData(LOAD_LLM_PROVIDERS_STARTED, LOAD_LLM_PROVIDERS, llmSettingsQuery, toProviders);

// updateSystemSettings replaces the WHOLE section, so a write built from a stale/empty
// client list would drop existing providers. Always read the section fresh right before
// writing. Rejects if the read fails, so we never overwrite with an incomplete list.
const fetchCurrentProviders = async () => {
    const response = await queryData(llmSettingsQuery);
    return (get(response, 'data.result.providers') || []).map(pickProvider);
};

const saveProviders = providers =>
    mutateData(UPDATE_LLM_SETTINGS_STARTED, UPDATE_LLM_SETTINGS, updateLlmSettingsMutation, 'LLM providers updated.')({
        settings: { providers },
    });

// updateSystemSettings returns the freshly-saved `llm` section, so refresh the list
// straight from the mutation response (a LOAD action) instead of firing a second read.
const providersLoaded = section => ({ type: LOAD_LLM_PROVIDERS, payload: Immutable(toProviders(section)) });

export const createLlmProvider = provider => async (dispatch) => {
    let current;
    try {
        current = await fetchCurrentProviders();
    } catch (error) {
        dispatch(showToastr({ severity: 'error', detail: 'Could not load current LLM providers. Nothing was changed.' }));
        return error instanceof Error ? error : new Error('Failed to load LLM providers.');
    }
    if (current.some(p => p.id === provider.id)) {
        dispatch(showToastr({ severity: 'error', detail: `A provider named '${provider.id}' already exists.` }));
        return new Error('Duplicate provider name.');
    }
    const result = await dispatch(saveProviders([...current, pickProvider(provider)]));
    if (result instanceof Error) return result;
    dispatch(providersLoaded(result));
    return result;
};

// `originalId` identifies the record to replace; the Name field (stored as `id`) is
// editable, so the new id may differ. Reject a rename that collides with another provider.
export const updateLlmProvider = (provider, originalId = provider.id) => async (dispatch) => {
    let current;
    try {
        current = await fetchCurrentProviders();
    } catch (error) {
        dispatch(showToastr({ severity: 'error', detail: 'Could not load current LLM providers. Nothing was changed.' }));
        return error instanceof Error ? error : new Error('Failed to load LLM providers.');
    }
    if (!current.some(p => p.id === originalId)) {
        dispatch(showToastr({ severity: 'error', detail: `A provider named '${originalId}' no longer exists.` }));
        return new Error('Provider not found.');
    }
    if (provider.id !== originalId && current.some(p => p.id === provider.id)) {
        dispatch(showToastr({ severity: 'error', detail: `A provider named '${provider.id}' already exists.` }));
        return new Error('Duplicate provider name.');
    }
    const next = current.map(p => (p.id === originalId ? pickProvider(provider) : p));
    const result = await dispatch(saveProviders(next));
    if (result instanceof Error) return result;
    dispatch(providersLoaded(result));
    return result;
};

export const deleteLlmProvider = id => async (dispatch) => {
    let current;
    try {
        current = await fetchCurrentProviders();
    } catch (error) {
        dispatch(showToastr({ severity: 'error', detail: 'Could not load current LLM providers. Nothing was changed.' }));
        return error instanceof Error ? error : new Error('Failed to load LLM providers.');
    }
    const result = await dispatch(saveProviders(current.filter(p => p.id !== id)));
    if (result instanceof Error) return result;
    dispatch(providersLoaded(result));
    return result;
};
